import { array, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { PopupListItem, Service } from 'webapp-types';
import { BillingFeature, ModelName } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import highlightResult from 'webapp/helpers/highlight-result';
import joinClasses from 'webapp/helpers/join-classes';
import visibleCheckItem from 'webapp/modifiers/visible-check-item';
import AvatarOrInitialsComponent from 'webapp/components/avatar-or-initials';
import BarLoaderComponent from 'webapp/components/bar-loader';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import DummyCellComponent from 'webapp/components/dummy-cell';
import FlexibleToggleCellComponent from 'webapp/components/flexible-toggle-cell';
import FormCheckboxComponent from 'webapp/components/form/checkbox';
import OptionsButtonComponent from 'webapp/components/options-button';

import type BatchSelect from 'webapp/models-custom/batch-select';
import type User from 'webapp/models-custom/user';

interface Args {
	model: User;
	batchSelect: BatchSelect<User>;
}

enum InvitationAction {
	ACCEPT = 'accept',
	REJECT = 'reject',
}

type Action =
	| 'openProfile'
	| 'resendInvitation'
	| 'editUser'
	| 'editClientAccess'
	| 'editSourceAccess'
	| 'deleteUser';
type Option = PopupListItem<Action>;

export default class AccountUsersTableRowComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('popup') declare popupService: Service.Popup;
	@service('billing') declare billingService: Service.Billing;
	@service('request') declare requestService: Service.Request;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('user-management') declare userManagementService: Service.UserManagement;

	@tracked isVisibleOnScreen = false;
	@tracked showAll = false;
	@tracked isInvitationProcessing = false;

	get userRecord() {
		return this.args.model.model;
	}

	get isDeletingInProgress() {
		return !!this.userRecord.isDeleted && !!this.userRecord.isSaving;
	}

	get user(): User {
		return this.args.model;
	}

	get skipMainAction() {
		return (
			this.options.length === 1 &&
			!!this.options.find((option) => option.action === 'deleteUser')
		);
	}

	get options(): Option[] {
		const options: Option[] = [],
			user = this.user;

		if (user.isMe) {
			options.push({
				icon: 'User',
				text: 'Profile',
				action: 'openProfile',
			});
		}

		if (user.canResendInvitationMail) {
			options.push({
				icon: 'Mail',
				text: 'Resend invitation',
				action: 'resendInvitation',
			});
		}

		if (!user.isAccountOwner && !user.isMe && !user.isRestricted) {
			options.push({
				icon: 'Pencil',
				text: 'Edit',
				action: 'editUser',
			});

			if (this.accountService.isAgency && !user.isAdmin) {
				options.push({
					icon: 'Clients',
					text: 'Edit account access',
					action: 'editClientAccess',
					isExternal: true,
					affixIcon: 'External',
				});
			}

			if (!user.isAdmin) {
				options.push({
					icon: 'UserAccess',
					text: 'Edit source access',
					action: 'editSourceAccess',
					isExternal: true,
					affixIcon: 'External',
				});
			}

			options.push({
				class: 'delete',
				text: 'Delete',
				action: 'deleteUser',
				icon: 'Trash',
			});
		}

		return options;
	}

	get isSelected(): boolean {
		const { batchSelect, model } = this.args;
		return batchSelect.isItemSelected(model);
	}

	get isSelectionDisabled(): boolean {
		return this.args.model.isMe || this.args.model.isParentUser;
	}

	handleUserInvitation(action: InvitationAction) {
		if (this.currentUserService.isVerified) {
			if (this.billingService.checkIfCan(BillingFeature.USERS, true)) {
				const actionString = action.toString();
				this.isInvitationProcessing = true;
				this.requestService
					.apiWithAsyncReply(
						'POST',
						`/users/${this.user.numericId}/invite-${actionString}`,
					)
					.then(() => {
						this.infolayerService.ok(
							`User <strong>${this.user.name}</strong> was ${actionString}ed!`,
						);
					})
					.catch(this.requestService.showAllValidationErrors.bind(this.requestService))
					.finally(() => {
						this.isInvitationProcessing = false;
					});
			}
		} else {
			this.currentUserService.openEmailVerificationInfoPopup('invite new users');
		}
	}

	@action
	doOptionsAction(actionName: string): void {
		this[actionName as Action]();
	}

	@action
	openProfile() {
		this.aiChatService.onNavigateToProfile();
	}

	@action
	editUser() {
		if (this.userManagementService.functions) {
			const user = this.user;

			this.userManagementService.functions
				.openEditUserDialog({
					user: {
						id: user.numericId!,
						email: user.email,
						name: user.name,
						avatar: user.avatar,
						role: user.level,
					},
				})
				.then(() => {
					void this.requestService.storeFindAll(ModelName.USER);
				})
				.catch(() => {
					// Dialog was closed/cancelled — no action needed
				});
		} else {
			this.accountService.openAccountAppEditUser(this.user.numericId!);
		}
	}

	@action
	editClientAccess() {
		this.accountService.openAccountAppEditClientAccess(this.user.numericId!);
	}

	@action
	editSourceAccess() {
		this.accountService.openAccountAppEditSourceAccess(this.user.numericId!);
	}

	@action
	deleteUser() {
		const user = this.user;

		const affirmativeFunc = () => {
			const userId = user.id,
				userEmail = user.email,
				layer = this.infolayerService.loading('Deleting user...');

			// asynced event
			this.requestService
				.apiWithAsyncReply('DELETE', `/users/${userId}`)
				.then(
					() => {
						this.analyticsService.event(TrackingEvent.USER_DELETED, {
							user_id: userId,
						});
						layer.ok(`User <strong>${userEmail}</strong> deleted!`);
						user.delete(true).catch(
							this.requestService.handleGenericThrownError.bind(this.requestService),
						);
					},
					(errorData) => {
						layer.close();
						throw errorData;
					},
				)
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
		};

		this.args.batchSelect.unselectAll();
		this.popupService.openConfirmPopup({
			onConfirm: affirmativeFunc,
			title: `Are you sure you want to delete the user "${user.email}"?`,
			text: '',
			confirmAction: 'Delete',
			cancelAction: 'Cancel',
		});
	}

	@action
	acceptInvitation() {
		this.handleUserInvitation(InvitationAction.ACCEPT);
	}

	@action
	rejectInvitation() {
		this.handleUserInvitation(InvitationAction.REJECT);
	}

	@action
	resendInvitation() {
		this.mmUsersService.sendUserInviteReminder(this.user.id!);
	}

	@action
	visibleChanged(isVisible: boolean): void {
		this.isVisibleOnScreen = isVisible;
	}

	@action
	toggleShowAll() {
		this.showAll = !this.showAll;
	}
	<template>
		{{#if this.isVisibleOnScreen}}

			<tr
				class={{joinClasses
					(array
						"table-row" (if this.showAll "all-shown") (if this.isSelected "selected")
					)
				}}
				data-id={{@model.id}}
			>
				<td class="cc first">
					<FormCheckboxComponent
						@name="users-table-row"
						@selected={{this.isSelected}}
						@isDisabled={{this.isSelectionDisabled}}
						@theme="blue"
						@onChange={{fn @batchSelect.changeSelection @model}}
					/>
				</td>

				<td class="c0 no-grow">
					<AvatarOrInitialsComponent @name={{@model.name}} @avatar={{@model.avatar}} />
				</td>
				<td class="c1">
					{{#if @model.isMe}}
						<span class="me">That's you</span>
					{{else}}
						<strong class="name">
							{{highlightResult text=@model.name match=@model.activeFilter}}
						</strong>
						{{#unless @model.activated}}
							<span class="pending">Pending</span>
						{{/unless}}
						<small>
							{{highlightResult text=@model.email match=@model.activeFilter}}
						</small>
					{{/if}}

				</td>
				<td class="c2 prio-low" data-label="Joined: ">
					{{@model.joinedText}}
				</td>
				<td class="c3 prio-medium" data-label="Last seen: ">
					{{@model.lastSeenText}}
				</td>
				<td class="c5 prio-medium" data-label="Shared boards: ">
					{{@model.allowedBoards.length}}
				</td>
				<td class="c6 prio-high" data-label="Role: ">
					{{@model.roleText}}
				</td>
				<td class="c7 button-ct last">
					{{#if @model.activated}}
						<OptionsButtonComponent
							@options={{this.options}}
							@onAction={{this.doOptionsAction}}
							@isDisabled={{this.isDeletingInProgress}}
							@skipMainAction={{this.skipMainAction}}
						/>
					{{else}}
						{{#if this.isInvitationProcessing}}
							<BarLoaderComponent />
						{{else}}
							<div class="button-ct__wrapper">
								<a
									role="button"
									class="accept"
									{{on "click" this.acceptInvitation}}
								>
									<CommonUiIconComponent @name="Check" @size="sm" />
									Accept
								</a>
								<a
									role="button"
									class="reject"
									{{on "click" this.rejectInvitation}}
								>
									Reject
								</a>
							</div>
						{{/if}}
					{{/if}}
				</td>
				<FlexibleToggleCellComponent
					@showAll={{this.showAll}}
					@toggleShowAll={{this.toggleShowAll}}
				/>

			</tr>
		{{else}}
			<tr class="table-row" {{visibleCheckItem this.visibleChanged}}>
				<DummyCellComponent />
			</tr>

		{{/if}}
	</template>
}
